import { UAParser } from 'ua-parser-js';
import { v4 as uuidv4 } from 'uuid';

export interface DeviceProfile {
  deviceId: string;
  userAgent: string;
  browser: {
    name?: string;
    version?: string;
  };
  os: {
    name?: string;
    version?: string;
  };
  device: {
    model?: string;
    type?: string;
    vendor?: string;
  };
  screen: {
    width: number;
    height: number;
    colorDepth: number;
    pixelRatio: number;
  };
  capabilities: {
    touchPoints: number;
    isTouch: boolean;
  };
  gpu?: string;
  language: string;
  timezone: string;
  isStandalone: boolean;
}

/**
 * Gets or creates a persistent device ID
 */
export const getOrGenerateDeviceId = (): string => {
  let deviceId = localStorage.getItem('deviceId');
  if (!deviceId) {
    deviceId = uuidv4();
    if (deviceId) {
      localStorage.setItem('deviceId', deviceId);
    }
  }
  return deviceId || uuidv4();
};

/**
 * Collects detailed device information
 */
export const collectDeviceProfile = (): DeviceProfile => {
  const parser = new UAParser();
  const result = parser.getResult();
  
  // GPU Info
  let gpu = 'Unknown';
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        gpu = (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      }
    }
  } catch (e) {
    console.warn('Could not collect GPU info:', e);
  }

  return {
    deviceId: getOrGenerateDeviceId(),
    userAgent: navigator.userAgent,
    browser: {
      name: result.browser.name,
      version: result.browser.version,
    },
    os: {
      name: result.os.name,
      version: result.os.version,
    },
    device: {
      model: result.device.model,
      type: result.device.type || 'desktop',
      vendor: result.device.vendor,
    },
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      colorDepth: window.screen.colorDepth,
      pixelRatio: window.devicePixelRatio,
    },
    capabilities: {
      touchPoints: navigator.maxTouchPoints || 0,
      isTouch: (navigator.maxTouchPoints || 0) > 0,
    },
    gpu,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    isStandalone: window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone || false,
  };
};
